// ./src/config/fuelGrades.ts
// Fuel grade configuration for vehicle refueling
// Supports international markets: North America, Europe, Russia/CIS, Latin America
// Used by refuel forms, selectors, and helper functions

// ============================================
// Types
// ============================================

export interface FuelGradeOption {
  /** Value stored in database */
  value: string;
  /** Translation key - use with t('common:fuelGrades.regular') */
  labelKey: string;
  /** Icon name for UI components */
  iconName: string;
  /** Fuel types this grade applies to (references fuelTypes.ts values) */
  fuelTypes: string[];
}

// ============================================
// Fuel Grade Options
// ============================================

export const FUEL_GRADE_OPTIONS: FuelGradeOption[] = [
  // Gasoline grades (ordered by octane level)
  { value: "regular", labelKey: "fuelGrades.regular", iconName: "GasStation", fuelTypes: ["gasoline"] },
  { value: "midgrade", labelKey: "fuelGrades.midgrade", iconName: "GasStation", fuelTypes: ["gasoline"] },
  { value: "premium", labelKey: "fuelGrades.premium", iconName: "GasStation", fuelTypes: ["gasoline"] },
  { value: "super", labelKey: "fuelGrades.super", iconName: "GasStation", fuelTypes: ["gasoline"] },
  // Diesel grades
  { value: "diesel", labelKey: "fuelGrades.diesel", iconName: "GasStation", fuelTypes: ["diesel"] },
  { value: "diesel-premium", labelKey: "fuelGrades.dieselPremium", iconName: "GasStation", fuelTypes: ["diesel"] },
  { value: "diesel-winter", labelKey: "fuelGrades.dieselWinter", iconName: "Snowflake", fuelTypes: ["diesel"] },
  // Ethanol blends (gasoline-based, also compatible with e85/flex-fuel vehicles)
  { value: "e5", labelKey: "fuelGrades.e5", iconName: "Leaf", fuelTypes: ["gasoline", "e85"] },
  { value: "e10", labelKey: "fuelGrades.e10", iconName: "Leaf", fuelTypes: ["gasoline", "e85"] },
  { value: "e15", labelKey: "fuelGrades.e15", iconName: "Leaf", fuelTypes: ["gasoline", "e85"] },
  { value: "e85", labelKey: "fuelGrades.e85", iconName: "Leaf", fuelTypes: ["e85"] },
  // Biodiesel blends (diesel-based)
  { value: "b7", labelKey: "fuelGrades.b7", iconName: "Leaf", fuelTypes: ["diesel", "biodiesel"] },
  { value: "b20", labelKey: "fuelGrades.b20", iconName: "Leaf", fuelTypes: ["diesel", "biodiesel"] },
  { value: "b100", labelKey: "fuelGrades.b100", iconName: "Leaf", fuelTypes: ["biodiesel"] },
  // Gas fuels
  { value: "lpg", labelKey: "fuelGrades.lpg", iconName: "Flame", fuelTypes: ["lpg"] },
  { value: "cng", labelKey: "fuelGrades.cng", iconName: "Flame", fuelTypes: ["cng"] },
  // Electric charging types
  { value: "standard", labelKey: "fuelGrades.standard", iconName: "BatteryCharging", fuelTypes: ["electric"] },
  { value: "fast", labelKey: "fuelGrades.fast", iconName: "Bolt", fuelTypes: ["electric"] },
  // Hydrogen
  { value: "h70", labelKey: "fuelGrades.h70", iconName: "Atom", fuelTypes: ["hydrogen"] },
  { value: "h35", labelKey: "fuelGrades.h35", iconName: "Atom", fuelTypes: ["hydrogen"] },
];

// ============================================
// Lookup Map
// ============================================

export const FUEL_GRADE_MAP: Record<string, FuelGradeOption> = Object.fromEntries(
  FUEL_GRADE_OPTIONS.map((opt) => [opt.value, opt])
);

// ============================================
// Helper Functions
// ============================================

/**
 * Get fuel grade option by value
 * @param value - Fuel grade value
 * @returns Fuel grade option or undefined
 */
export const getFuelGradeOption = (
  value: string | null | undefined
): FuelGradeOption | undefined => {
  if (!value) return undefined;
  return FUEL_GRADE_MAP[value];
};

/**
 * Get icon name for a fuel grade
 * @param value - Fuel grade value
 * @returns Icon name or default
 */
export const getFuelGradeIcon = (value: string | null | undefined): string => {
  return getFuelGradeOption(value)?.iconName ?? "GasStation";
};

/**
 * Get fuel grades filtered by fuel type
 * @param fuelType - Fuel type to filter by (e.g., "gasoline", "diesel")
 * @returns Array of fuel grade options that match the fuel type
 */
export const getFuelGradesByType = (
  fuelType: string | null | undefined
): FuelGradeOption[] => {
  if (!fuelType) return FUEL_GRADE_OPTIONS;
  return FUEL_GRADE_OPTIONS.filter((opt) => opt.fuelTypes.includes(fuelType));
};